<script lang="ts">
  import type { Snippet } from 'svelte';
  import Sparkline from '../charts/Sparkline.svelte';
  import InfoTip from './InfoTip.svelte';

  // A single labelled figure — the KPI card used across the dashboard and drawers.
  // An optional `spark` renders a small hoverable trend sparkline beneath; an
  // optional `delta` shows a coloured "(+X)" change next to the value. `color`
  // (a CSS colour) accents the value + sparkline, overriding `tone` — used for
  // the role cards, which carry their own quadrant colour. An optional `icon`
  // snippet renders before the label (tinted with `color`); `info` adds a hover
  // tooltip explaining the metric.
  let {
    label,
    value,
    sub = null,
    tone = 'default',
    color = null,
    delta = null,
    deltaTone = 'default',
    spark = null,
    sparkLabels = null,
    sparkFormat = (v: number) => String(v),
    icon = null,
    info = null,
    action = null,
    redact = false,
  }: {
    label: string;
    value: string;
    sub?: string | null;
    tone?: 'default' | 'primary' | 'warning' | 'success';
    color?: string | null;
    delta?: string | null;
    deltaTone?: 'default' | 'primary' | 'warning' | 'success';
    spark?: number[] | null;
    sparkLabels?: (string | number)[] | null;
    sparkFormat?: (v: number) => string;
    icon?: Snippet | null;
    info?: string | null;
    action?: Snippet | null;
    /** Blur the figure and its sub-line in private mode. OFF by default: almost every
     *  StatCard on the dashboard is a COUNT, and counts stay readable — a demo of an
     *  analytics product with no numbers in it is not a demo. Set it on the few that
     *  carry money (`fmtUsd`), which is the one class of figure that must not be
     *  legible on a public recording. */
    redact?: boolean;
  } = $props();

  const TONES = { default: '', primary: 'text-primary', warning: 'text-warning', success: 'text-success' };
  const toneClass = $derived(TONES[tone]);
  const deltaClass = $derived(TONES[deltaTone] || 'text-base-content/62');
</script>

<div class="relative rounded-box border hair bg-base-100 px-4 py-3">
  {#if info}<InfoTip class="absolute right-2 top-2" text={info} />{/if}
  <div class="eyebrow flex items-center gap-1.5 {spark && spark.length > 1 ? 'mb-2' : 'mb-1.5'} {info ? 'pr-4' : ''}">
    {#if icon}<span class="shrink-0" style:color={color || null}>{@render icon()}</span>{/if}
    <span class="min-w-0 truncate">{label}</span>
  </div>
  {#if spark && spark.length > 1}
    <!-- Sparkline sits between the title and the value when present. `color`
         drives currentColor via the wrapper; otherwise fall back to the tone. -->
    <div class="mb-2 {color ? '' : toneClass || 'text-base-content/58'}" style:color={color || null}>
      <Sparkline values={spark} labels={sparkLabels} format={sparkFormat} class="h-7 w-full" />
    </div>
  {/if}
  <!-- Money is redacted, counts are not — see the `redact` prop. The LABEL and the
       sparkline are never redacted either way: they say which metric this is and
       which way it is moving, which is the part a demo needs to survive. -->
  <div
    class="{redact ? 'redact ' : ''}font-display text-[1.6rem] font-bold leading-none tabular-nums {color ? '' : toneClass}"
    style:color={color || null}
  >
    {value}{#if delta}<span class="ml-1.5 align-baseline font-mono text-base font-semibold {deltaClass}">({delta})</span>{/if}
  </div>
  <!-- The sub-line follows the figure it belongs to: on a money card it is the
       breakdown of that money, on a count card it is "12 of 340 members". -->
  {#if sub}<div class="{redact ? 'redact ' : ''}mt-1 font-mono text-[0.7rem] leading-tight text-base-content/65">{sub}</div>{/if}
  {#if action}<div class="mt-2">{@render action()}</div>{/if}
</div>
